import {
  type AdminUserRow,
  type AuthAccount,
  type TenantRole,
  type UserRole,
  type UserStatus,
} from "@nexys/user-management-client";
import {
  AlertCircle,
  Building2,
  Fingerprint,
  KeyRound,
  Mail,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative, initialsOf } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { StatusBadge, TenantRoleBadge, UserRoleBadge } from "../roles/role-badge.js";
import { TenantRoleSelect, UserRoleSelect } from "../roles/role-select.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Avatar, AvatarFallback } from "../ui/avatar.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import { Table, TableBody, TableCell, TableRow } from "../ui/table.js";
import { UserDeleteDialog } from "./user-delete-dialog.js";

export interface UserDetailViewProps {
  userId: string;
  /** Called after a successful delete — navigate back to the list. */
  onDeleted?: () => void;
}

/**
 * Full-page admin view of one user: profile, status, global role,
 * tenant memberships, and sign-in methods. Superadmin surface — the
 * page hosting it should gate on the role.
 */
export const UserDetailView = ({ userId, onDeleted }: UserDetailViewProps) => {
  const { data: user, error, loading, refresh } = useCall("adminGetUser", { userId });

  if (loading) {
    return (
      <div className="grid gap-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-14 rounded-full" />
          <div className="grid gap-2">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-3.5 w-60" />
          </div>
        </div>
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }
  if (error || !user) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>
          {error ? errorMessage(error) : "User not found."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center gap-4">
        <Avatar className="size-14 text-base">
          <AvatarFallback>{initialsOf(user.name, user.email)}</AvatarFallback>
        </Avatar>
        <div className="grid gap-0.5">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">{user.name || user.email}</h2>
            <StatusBadge status={user.status} />
            <UserRoleBadge role={user.role} />
          </div>
          <p className="text-sm text-muted-foreground">
            {user.email} · joined {formatRelative(user.createdAt)}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ProfileCard user={user} onSaved={refresh} />
        <AccessCard user={user} onChanged={refresh} />
      </div>
      <AuthMethodsCard user={user} onChanged={refresh} />
      <TenantsCard userId={user.id} />
      <DangerCard user={user} onDeleted={onDeleted} />
    </div>
  );
};

/* ── profile ─────────────────────────────────────────────────────── */

const ProfileCard = ({ user, onSaved }: { user: AdminUserRow; onSaved: () => void }) => {
  const client = useUmClient();
  // Older accounts only carry a display name — seed first from it.
  const [firstName, setFirstName] = useState(user.firstName ?? user.name);
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [email, setEmail] = useState(user.email);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    firstName.trim() !== (user.firstName ?? user.name) ||
    lastName.trim() !== (user.lastName ?? "") ||
    email.trim().toLowerCase() !== user.email;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.adminUpdateUser({
      userId: user.id,
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      email: email.trim().toLowerCase() !== user.email ? email.trim() : undefined,
    });
    setLoading(false);
    if (res.ok) onSaved();
    else setError(errorMessage(res.error, "Couldn't update user."));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Name and email. The email stays verified.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor="detail-first-name" required>
              <Input
                id="detail-first-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label="Last name" htmlFor="detail-last-name">
              <Input
                id="detail-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Email" htmlFor="detail-email" required>
            <Input
              id="detail-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <div>
            <Button
              type="submit"
              loading={loading}
              disabled={!dirty || !firstName.trim() || !email.trim()}
            >
              Save changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

/* ── status + global role ────────────────────────────────────────── */

const STATUSES: UserStatus[] = ["active", "inactive", "pending"];

const AccessCard = ({ user, onChanged }: { user: AdminUserRow; onChanged: () => void }) => {
  const client = useUmClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (key: string, op: () => Promise<{ ok: boolean; error?: unknown }>) => {
    setBusy(key);
    setError(null);
    const res = await op();
    setBusy(null);
    if (res.ok) onChanged();
    else setError(errorMessage(res.error as never, "Action failed."));
  };

  const setStatus = (status: UserStatus) =>
    run("status", () => client.adminSetUserStatus({ userId: user.id, status }));
  const setRole = (role: UserRole) =>
    run("role", () => client.adminSetUserRole({ userId: user.id, role }));
  const revoke = () =>
    run("revoke", () => client.adminRevokeUserTokens({ userId: user.id }));

  // active/inactive → pending is rejected by the server.
  const allowed = STATUSES.filter(
    (s) => s === user.status || s !== "pending" || user.status === "pending"
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access</CardTitle>
        <CardDescription>
          Status, system role and sessions. Deactivating revokes all sessions.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <Field label="Status" htmlFor="detail-status">
          <Select
            value={user.status}
            onValueChange={(v) => void setStatus(v as UserStatus)}
            disabled={busy !== null}
          >
            <SelectTrigger id="detail-status" className="h-8 w-[10.5rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allowed.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="System role" htmlFor="detail-role">
          <UserRoleSelect value={user.role} onChange={(r) => void setRole(r)} disabled={busy !== null} />
        </Field>
        <div>
          <Button variant="outline" size="sm" onClick={() => void revoke()} loading={busy === "revoke"}>
            <KeyRound />
            Revoke all sessions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── auth methods ────────────────────────────────────────────────── */

const providerLabel = (p: string) => (p === "credential" ? "password" : p);

const SOCIAL_AUTH_TYPES = ["google", "microsoft", "github", "discord", "facebook"];

const AuthMethodsCard = ({ user, onChanged }: { user: AdminUserRow; onChanged: () => void }) => {
  const client = useUmClient();
  const { data, error, loading, refresh } = useCall("adminAuthMethods", { userId: user.id });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (key: string, op: () => Promise<{ ok: boolean; error?: unknown }>) => {
    setBusy(key);
    setActionError(null);
    setNotice(null);
    const res = await op();
    setBusy(null);
    if (res.ok) {
      await refresh();
      onChanged();
    } else {
      setActionError(errorMessage(res.error as never, "Action failed."));
    }
    return res.ok;
  };

  const methodCount = (data?.accounts.length ?? 0) + (data?.passkeys.length ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="size-4 text-muted-foreground" />
          Sign-in methods
        </CardTitle>
        <CardDescription>
          How this user can authenticate. A user always keeps at least one
          method. A social login links for good once the user signs in with
          the provider; until then a pre-link only matches when the provider
          reports the pre-linked email as that user's verified address.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
        {actionError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}
        {notice ? (
          <Alert variant="success">
            <ShieldCheck />
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : data ? (
          <>
            <PasswordRow
              hasCredential={data.accounts.some((a) => a.providerId === "credential")}
              busy={busy !== null}
              settingBusy={busy === "pw-set"}
              emailBusy={busy === "pw-email"}
              onSet={(password) =>
                void run("pw-set", () =>
                  client.adminSetUserPassword({ userId: user.id, password })
                ).then((ok) => {
                  if (ok) setNotice("Password updated — the user's sessions were signed out.");
                })
              }
              onSendReset={() =>
                void run("pw-email", () =>
                  client.adminSetUserPassword({ userId: user.id, sendResetEmail: true })
                ).then((ok) => {
                  if (ok) setNotice(`Reset link emailed to ${user.email}.`);
                })
              }
            />
            <AddMethodRow
              busy={busy !== null}
              onAdd={(authType, value) =>
                void run("add", () =>
                  client.adminAddAccount({ userId: user.id, authType, value })
                )
              }
            />
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableBody>
                  {data.accounts.map((a) => (
                    <MethodRow
                      key={a.id}
                      icon={<KeyRound className="size-4 text-muted-foreground" />}
                      label={providerLabel(a.providerId)}
                      detail={<AccountDetail account={a} />}
                      action={
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Unlink ${providerLabel(a.providerId)}`}
                          onClick={() =>
                            void run(a.id, () =>
                              client.adminUnlinkAccount({ userId: user.id, accountId: a.id })
                            )
                          }
                          loading={busy === a.id}
                          disabled={(busy !== null && busy !== a.id) || methodCount <= 1}
                        >
                          <X className="text-destructive" />
                        </Button>
                      }
                    />
                  ))}
                  {data.passkeys.map((p) => (
                    <MethodRow
                      key={p.id}
                      icon={<Fingerprint className="size-4 text-muted-foreground" />}
                      label={p.name || "passkey"}
                      detail={`webauthn · ${p.deviceType}${p.createdAt ? ` · added ${formatRelative(p.createdAt)}` : ""}`}
                      action={
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Remove passkey"
                          onClick={() =>
                            void run(p.id, () =>
                              client.adminDeletePasskey({ userId: user.id, passkeyId: p.id })
                            )
                          }
                          loading={busy === p.id}
                          disabled={(busy !== null && busy !== p.id) || methodCount <= 1}
                        >
                          <Trash2 className="text-destructive" />
                        </Button>
                      }
                    />
                  ))}
                  {methodCount === 0 ? (
                    <TableRow>
                      <TableCell className="p-0">
                        <EmptyState
                          icon={Fingerprint}
                          title="No sign-in methods"
                          description="This user can't sign in."
                          className="border-0 rounded-none bg-transparent"
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck className="size-4 text-muted-foreground" />
                Two-factor authentication
                <Badge variant={data.twoFactorEnabled ? "default" : "outline"}>
                  {data.twoFactorEnabled ? "enabled" : "off"}
                </Badge>
              </div>
              {data.twoFactorEnabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void run("2fa", () =>
                      client.adminDisableTwoFactor({ userId: user.id })
                    )
                  }
                  loading={busy === "2fa"}
                >
                  Disable 2FA
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

/**
 * Password management: set (or overwrite) the user's password
 * directly, or email them a reset link. The emailed token also works
 * for accounts with no password yet — consuming it creates the
 * credential — so both actions are always available.
 */
const PasswordRow = ({
  hasCredential,
  busy,
  settingBusy,
  emailBusy,
  onSet,
  onSendReset,
}: {
  hasCredential: boolean;
  busy: boolean;
  settingBusy: boolean;
  emailBusy: boolean;
  onSet: (password: string) => void;
  onSendReset: () => void;
}) => {
  const [password, setPassword] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return;
    onSet(password);
    setPassword("");
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-end gap-3 rounded-lg border px-4 py-3 sm:grid-cols-[1fr_auto_auto]"
    >
      <Field
        label={hasCredential ? "Reset password" : "Set password"}
        htmlFor="admin-set-password"
        description={
          hasCredential
            ? "Replaces the current password and signs the user out everywhere."
            : "This account has no password yet."
        }
      >
        <Input
          id="admin-set-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />
      </Field>
      <Button type="submit" size="sm" disabled={password.length < 8 || busy} loading={settingBusy}>
        <KeyRound />
        {hasCredential ? "Reset" : "Set"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onSendReset}
        disabled={busy}
        loading={emailBusy}
      >
        <Mail />
        Email reset link
      </Button>
    </form>
  );
};

/**
 * `{authType, value}` entry row for social pre-links: the value is the
 * provider handle (GitHub/Discord username), the provider-side account
 * id, or an email (the user's first sign-in with that provider lands on
 * this account). Passwords are managed by `PasswordRow` above.
 */
const AddMethodRow = ({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (authType: string, value: string) => void;
}) => {
  const [authType, setAuthType] = useState("");
  const [value, setValue] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!authType || !value.trim()) return;
    onAdd(authType, value.trim());
    setAuthType("");
    setValue("");
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-end gap-3 rounded-lg border border-dashed bg-muted/30 p-3 sm:grid-cols-[auto_1fr_auto]"
    >
      <Field label="Pre-link provider" htmlFor="add-method-type">
        <Select value={authType} onValueChange={setAuthType}>
          <SelectTrigger id="add-method-type" className="h-8 w-[10.5rem]">
            <SelectValue placeholder="Provider…" />
          </SelectTrigger>
          <SelectContent>
            {SOCIAL_AUTH_TYPES.map((t) => (
              <SelectItem key={t} value={t} className="capitalize">
                {providerLabel(t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Username, account id or email" htmlFor="add-method-value">
        <Input
          id="add-method-value"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="octocat or user@gmail.com"
          autoComplete="off"
        />
      </Field>
      <Button type="submit" size="sm" disabled={!authType || !value.trim() || busy}>
        <Plus />
        Add
      </Button>
    </form>
  );
};

/**
 * Whether a social row already holds the provider's own subject id —
 * numeric for GitHub/Google/Discord/Facebook, a GUID for Microsoft —
 * or still an admin-typed pre-link (a handle or an email) waiting for
 * the user's first sign-in to re-key it.
 */
const isProviderSubject = (providerId: string, accountId: string) =>
  providerId === "microsoft"
    ? /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(accountId)
    : /^\d+$/.test(accountId);

/**
 * What a social row is keyed on. A real login stores the IdP subject
 * (GitHub's numeric id, Google's `sub`); an admin pre-link stores the
 * handle or email typed in, which resolves on the user's first sign-in
 * when the provider reports that handle, or that email as verified.
 */
const AccountDetail = ({ account }: { account: AuthAccount }) => {
  const added = `added ${formatRelative(account.createdAt)}`;
  if (!account.accountId) return <>{added}</>;
  const linked = isProviderSubject(account.providerId, account.accountId);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <code
        className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground"
        title={linked ? "Provider account id" : "Pre-linked on this handle or email"}
      >
        {account.accountId}
      </code>
      {linked ? (
        <Badge variant="success">linked</Badge>
      ) : (
        <Badge variant="muted">pre-link · awaiting first sign-in</Badge>
      )}
      <span>{added}</span>
    </span>
  );
};

const MethodRow = ({
  icon,
  label,
  detail,
  action,
}: {
  icon: ReactNode;
  label: string;
  detail: ReactNode;
  action: ReactNode;
}) => (
  <TableRow>
    <TableCell>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium capitalize">{label}</span>
      </div>
    </TableCell>
    <TableCell className="text-sm text-muted-foreground">{detail}</TableCell>
    <TableCell className="text-right">{action}</TableCell>
  </TableRow>
);

/* ── tenants ─────────────────────────────────────────────────────── */

const TenantsCard = ({ userId }: { userId: string }) => {
  const client = useUmClient();
  const memberships = useCall("adminListUserTenants", { userId });
  const tenants = useCall("adminListTenants");
  const [orgId, setOrgId] = useState("");
  const [role, setRole] = useState<TenantRole>("member");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const memberOf = new Set(
    (memberships.data?.memberships ?? []).map((m) => m.organizationId)
  );
  const assignable = useMemo(
    () => (tenants.data?.tenants ?? []).filter((t) => !memberOf.has(t.id)),
    [tenants.data, memberships.data]
  );

  const assign = async () => {
    if (!orgId) return;
    setBusy("assign");
    setError(null);
    const res = await client.addMember({ organizationId: orgId, userId, role });
    setBusy(null);
    if (res.ok) {
      setOrgId("");
      setRole("member");
      await memberships.refresh();
    } else {
      setError(errorMessage(res.error, "Couldn't assign tenant."));
    }
  };

  const remove = async (memberId: string, organizationId: string) => {
    setBusy(memberId);
    setError(null);
    const res = await client.removeMember({ memberId, organizationId });
    setBusy(null);
    if (res.ok) await memberships.refresh();
    else setError(errorMessage(res.error, "Couldn't remove from tenant."));
  };

  const rows = memberships.data?.memberships ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="size-4 text-muted-foreground" />
          Tenants
        </CardTitle>
        <CardDescription>Memberships and tenant-level roles.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid items-end gap-3 rounded-lg border border-dashed bg-muted/30 p-3 sm:grid-cols-[1fr_auto_auto]">
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Pick a tenant…" />
            </SelectTrigger>
            <SelectContent>
              {assignable.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <TenantRoleSelect value={role} onChange={setRole} />
          <Button
            size="sm"
            onClick={() => void assign()}
            loading={busy === "assign"}
            disabled={!orgId || busy !== null}
          >
            <Plus />
            Assign
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableBody>
              {memberships.loading ? (
                <TableRow>
                  <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell className="p-0">
                    <EmptyState
                      icon={Building2}
                      title="No tenants"
                      description="This user isn't a member of any tenant yet."
                      className="border-0 rounded-none bg-transparent"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((m) => (
                  <TableRow key={m.memberId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="size-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{m.organizationName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TenantRoleBadge role={m.role} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      joined {formatRelative(m.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => void remove(m.memberId, m.organizationId)}
                        loading={busy === m.memberId}
                        disabled={busy !== null && busy !== m.memberId}
                        aria-label={`Remove from ${m.organizationName}`}
                      >
                        <Trash2 className="text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── danger zone ─────────────────────────────────────────────────── */

const DangerCard = ({ user, onDeleted }: { user: AdminUserRow; onDeleted?: () => void }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Soft-deletes the account and revokes every session. The record is
          kept for audit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="destructive" onClick={() => setConfirming(true)}>
          <Trash2 />
          Delete user
        </Button>
        <UserDeleteDialog
          user={confirming ? user : null}
          onOpenChange={(open) => {
            if (!open) setConfirming(false);
          }}
          onDeleted={onDeleted}
        />
      </CardContent>
    </Card>
  );
};
